<script setup>
import { useShareLink } from "@/composables/useShareLink";
import { computed } from "vue";
import dayjs from "dayjs";
import { useModalogStore } from "@/stores/modalog.js";
import { useMastheadImageUrls } from "@/composables/useMastheadImageUrls.js";
import ExpertListCover from "@/Components/Profile/ExpertListCover.vue";
import IconExpertShield from "~/svg/icon-expert-shield-inverse.svg";
import IconPrivacyPrivate from "~/svg/privacy-private.svg";
import IconPrivacyPublic from "~/svg/privacy-public.svg";

const props = defineProps({
    list: {
        type: Object,
        required: true,
    },
    // Owners get the edit + change-cover buttons.
    isOwner: {
        type: Boolean,
        default: false,
    },
    // "full" renders the desktop banner + the mobile title block;
    // "mobile-banner" renders only the cover card, sized for the hero's
    // mobile gradient band (GH-1079) — pages drop it into the
    // WishListDetailHero #mobile-band slot.
    variant: {
        type: String,
        default: "full",
        validator: (value) => ["full", "mobile-banner"].includes(value),
    },
    // Editorial header for expert lists owned by Gift Heroes (GH-1299): the
    // cover art replaces the masthead photo and an "Expert List" eyebrow
    // sits above the title. Pages decide (list type + owner flag).
    expert: {
        type: Boolean,
        default: false,
    },
});

const { showModalog } = useModalogStore();

const isPrivate = computed(() => props.list.privacy === "private");

const formattedEventDate = computed(() =>
    props.list.event_date
        ? dayjs(props.list.event_date).format("MMMM D, YYYY")
        : null,
);

// Owner-chosen focal point (BH-38); null falls back to auto gravity.
const mastheadFocus = computed(() =>
    Number.isFinite(props.list.masthead_focus_x) &&
    Number.isFinite(props.list.masthead_focus_y)
        ? { x: props.list.masthead_focus_x, y: props.list.masthead_focus_y }
        : null,
);

// Cloudinary URLs derived from the masthead photo uuid; null when unset.
const mastheadUrls = computed(() =>
    props.list.masthead_photo
        ? useMastheadImageUrls(props.list.masthead_photo, mastheadFocus.value)
        : null,
);

// useMastheadImageUrls returns css url(...) strings for background-image use.
const bannerImage = computed(() => mastheadUrls.value?.large2x ?? null);
// The expert cover sets the photo into an <img> panel instead.
const coverPhotoSrc = computed(() => mastheadUrls.value?.large2xSrc ?? null);
const coverPhotoPosition = computed(
    () => mastheadUrls.value?.position ?? "center",
);
const bannerStyle = computed(() =>
    bannerImage.value
        ? {
              backgroundImage: bannerImage.value,
              backgroundPosition: mastheadUrls.value.position,
          }
        : {},
);

function editList() {
    showModalog({
        type: "ModalogCreateListBrand",
        props: {
            isEdit: true,
            list: props.list,
            listType: props.list.type,
        },
    });
}

function changeCover() {
    showModalog({
        type: "ModalogMastheadImageUpload",
        props: { list: props.list },
        isConfirmDisabled: true,
    });
}

// Visitor share button on the mobile cover (GH-1079). The masthead only
// renders on the list's own page, so the canonical URL is the share target.
const { shareLink } = useShareLink();

async function shareList() {
    // Tag the link before branching so one press mints one token,
    // whichever path the browser takes (GH-1192).
    const channel = navigator.share ? "share_sheet" : "copy_link";
    const url = shareLink(
        props.list.open_graph?.url ?? window.location.href,
        { type: "giftlist", uuid: props.list.uuid },
        channel,
    );

    if (navigator.share) {
        try {
            await navigator.share({ title: props.list.name, url });
        } catch {
            // Dismissing the share sheet rejects; nothing to clean up.
        }

        return;
    }

    await navigator.clipboard?.writeText(url);
}
</script>

<template>
    <header :class="variant === 'mobile-banner' ? 'px-2 pb-2' : ''">
        <div
            class="relative overflow-hidden"
            :class="
                variant === 'mobile-banner'
                    ? 'h-[158px] rounded-[5px]'
                    : 'h-[158px] max-lg:hidden lg:h-[320px] lg:rounded-t-[5px]'
            "
        >
            <!-- Expert lists by Gift Heroes get the editorial cover art with
                 the photo inset (GH-1299) -->
            <div
                v-if="expert"
                class="absolute inset-0"
                data-test="masthead-expert-cover"
            >
                <ExpertListCover
                    :image="coverPhotoSrc"
                    :image-position="coverPhotoPosition"
                    layout="banner"
                />
            </div>
            <template v-else>
                <!-- Masthead photo (or navy fallback) with a left-hand shade so
                     the desktop title stays legible on any image -->
                <div
                    class="absolute inset-0 bg-cover"
                    :class="bannerImage ? '' : 'bg-brand-navy'"
                    :style="bannerStyle"
                    data-test="masthead-cover"
                ></div>
                <div
                    class="absolute inset-0 max-lg:hidden"
                    style="
                        background-image: linear-gradient(
                            71deg,
                            rgba(16, 42, 61, 0.98) 12.5%,
                            rgba(16, 42, 61, 0) 80%
                        );
                    "
                ></div>
            </template>

            <!-- Desktop: title, description, privacy inside the banner -->
            <div
                class="absolute inset-y-0 left-[44px] flex flex-col justify-center max-lg:hidden"
                :class="
                    expert ? 'w-[491px] max-w-[55%]' : 'w-[424px] max-w-[60%]'
                "
            >
                <p
                    v-if="expert"
                    class="mb-4 inline-flex items-center gap-2 font-brand-display text-[18px] font-bold italic uppercase leading-none text-[#ffb545]"
                    data-test="masthead-eyebrow"
                >
                    <IconExpertShield
                        class="h-[21px] w-[17px] shrink-0"
                        aria-hidden="true"
                    />
                    Expert List
                </p>
                <h1
                    class="font-brand-display text-[42px] font-black italic uppercase leading-[1.05] text-white"
                >
                    {{ list.name }}
                </h1>
                <p
                    v-if="list.description"
                    class="mt-6 line-clamp-2 font-brand text-[15px] leading-[1.35] text-[#d8dae5]"
                >
                    {{ list.description }}
                </p>
                <div class="mt-5 flex flex-wrap items-center gap-2">
                    <span
                        class="inline-flex w-fit items-center gap-1.5 rounded-[2px] border border-solid border-[#e9e8e0] px-2.5 py-1 font-brand text-[11px] font-medium capitalize text-white opacity-60"
                    >
                        <IconPrivacyPrivate
                            v-if="isPrivate"
                            class="h-[9.5px] w-[11px] shrink-0"
                            aria-hidden="true"
                        />
                        <IconPrivacyPublic
                            v-else
                            class="size-[10.3px] shrink-0"
                            aria-hidden="true"
                        />
                        {{ list.privacy }}
                    </span>
                    <span
                        v-if="formattedEventDate"
                        class="inline-flex w-fit items-center gap-1.5 rounded-[2px] bg-[#ffb545] px-2.5 py-1 font-brand text-[11px] font-semibold text-[#102a3d]"
                    >
                        🎉 Event Date: {{ formattedEventDate }}
                    </span>
                </div>
            </div>

            <button
                v-if="!isOwner && variant === 'mobile-banner'"
                type="button"
                class="absolute bottom-2 right-2 grid size-10 place-items-center rounded-[5px] bg-black/50 text-white transition hover:bg-black/70"
                aria-label="Share list"
                @click="shareList"
            >
                <svg
                    viewBox="0 0 14 18"
                    class="h-[17px] w-[13px]"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="1.4"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    aria-hidden="true"
                >
                    <path d="M7 11V1.5M7 1.5 3.5 5M7 1.5 10.5 5" />
                    <path
                        d="M1 8v7.5A1.5 1.5 0 0 0 2.5 17h9a1.5 1.5 0 0 0 1.5-1.5V8"
                    />
                </svg>
            </button>
            <template v-if="isOwner">
                <button
                    type="button"
                    class="absolute grid place-items-center rounded-[5px] bg-black/50 text-white transition hover:bg-black/70 max-lg:bottom-2 max-lg:left-2 max-lg:size-10 lg:right-[23px] lg:top-[23px] lg:size-10"
                    aria-label="Edit wish list"
                    @click="editList"
                >
                    <svg
                        viewBox="0 0 10 10"
                        class="size-[15px]"
                        fill="currentColor"
                        aria-hidden="true"
                    >
                        <path
                            d="M6.58089 1.09767L7.35833 0.320225C7.45964 0.218715 7.57996 0.138181 7.71243 0.0832325C7.84489 0.0282839 7.98689 0 8.1303 0C8.27371 0 8.41571 0.0282839 8.54818 0.0832325C8.68064 0.138181 8.80097 0.218715 8.90227 0.320225L9.67972 1.09767C10.1068 1.52472 10.1068 2.21457 9.67972 2.64162L8.90227 3.41907L6.58089 1.09767ZM5.80345 1.87512L0 7.67861V10H2.32138L8.12483 4.19651L5.80345 1.87512Z"
                        />
                    </svg>
                </button>
                <button
                    type="button"
                    class="absolute bottom-2 right-2 grid size-10 place-items-center rounded-[5px] bg-black/50 text-white transition hover:bg-black/70 lg:bottom-[23px] lg:right-[23px]"
                    aria-label="Change cover photo"
                    @click="changeCover"
                >
                    <svg
                        viewBox="0 0 14 18"
                        class="h-[18px] w-[14px]"
                        fill="currentColor"
                        aria-hidden="true"
                    >
                        <path
                            d="M2.85 15H10.85C11.0667 15 11.221 14.9083 11.313 14.725C11.405 14.5417 11.384 14.3667 11.25 14.2L8.85 11.025C8.75 10.8917 8.61667 10.825 8.45 10.825C8.28333 10.825 8.15 10.8917 8.05 11.025L6.25 13.45C6.15 13.5833 6.01667 13.65 5.85 13.65C5.68334 13.65 5.55 13.5833 5.45 13.45L4.65 12.375C4.55 12.2417 4.41667 12.175 4.25 12.175C4.08334 12.175 3.95 12.2417 3.85 12.375L2.45 14.2C2.31667 14.3667 2.29567 14.5417 2.387 14.725C2.47834 14.9083 2.63267 15 2.85 15ZM2 18C1.45 18 0.979002 17.8043 0.587002 17.413C0.195002 17.0217 -0.000664969 16.5507 1.69779e-06 16V6.00001C1.69779e-06 5.45001 0.196002 4.97935 0.588002 4.58801C0.980002 4.19668 1.45067 4.00068 2 4.00001H12C12.55 4.00001 13.021 4.19601 13.413 4.58801C13.805 4.98001 14.0007 5.45068 14 6.00001V16C14 16.55 13.8043 17.021 13.413 17.413C13.0217 17.805 12.5507 18.0007 12 18H2ZM0.287002 1.71201C0.0956683 1.52068 1.69779e-06 1.28335 1.69779e-06 1.00001C1.69779e-06 0.71668 0.0960018 0.479347 0.288002 0.288014C0.480002 0.0966805 0.717335 0.000680365 1 1.36986e-05C1.28267 -0.000652968 1.52033 0.0953472 1.713 0.288014C1.90567 0.48068 2.00133 0.718014 2 1.00001C1.99867 1.28201 1.90267 1.51968 1.712 1.71301C1.52133 1.90635 1.284 2.00201 1 2.00001C0.716002 1.99801 0.478668 1.90201 0.288002 1.71201M4.288 1.71201C4.096 1.52135 4 1.28401 4 1.00001C4 0.716014 4.096 0.47868 4.288 0.288014C4.48 0.0973472 4.71734 0.00134703 5 1.36986e-05C5.28267 -0.00131963 5.52034 0.0946805 5.713 0.288014C5.90567 0.481347 6.00134 0.71868 6 1.00001C5.99867 1.28135 5.90267 1.51901 5.712 1.71301C5.52134 1.90701 5.284 2.00268 5 2.00001C4.716 1.99735 4.47867 1.90135 4.288 1.71201ZM8.288 1.71201C8.096 1.52135 8 1.28401 8 1.00001C8 0.716014 8.096 0.47868 8.288 0.288014C8.48 0.0973472 8.71734 0.00134703 9 1.36986e-05C9.28267 -0.00131963 9.52034 0.0946805 9.713 0.288014C9.90567 0.481347 10.0013 0.71868 10 1.00001C9.99867 1.28135 9.90267 1.51901 9.712 1.71301C9.52134 1.90701 9.284 2.00268 9 2.00001C8.716 1.99735 8.47867 1.90135 8.288 1.71201ZM12.288 1.71201C12.096 1.52135 12 1.28401 12 1.00001C12 0.716014 12.096 0.47868 12.288 0.288014C12.48 0.0973472 12.7173 0.00134703 13 1.36986e-05C13.2827 -0.00131963 13.5203 0.0946805 13.713 0.288014C13.9057 0.481347 14.0013 0.71868 14 1.00001C13.9987 1.28135 13.9027 1.51901 13.712 1.71301C13.5213 1.90701 13.284 2.00268 13 2.00001C12.716 1.99735 12.4787 1.90135 12.288 1.71201Z"
                        />
                    </svg>
                </button>
            </template>
        </div>

        <!-- Mobile: title, description, privacy below the banner -->
        <div v-if="variant === 'full'" class="px-4 pb-2 pt-6 lg:hidden">
            <p
                v-if="expert"
                class="mb-3 inline-flex items-center gap-2 font-brand-display text-[16px] font-bold italic uppercase leading-none text-[#db7712]"
                data-test="masthead-eyebrow"
            >
                <IconExpertShield
                    class="h-[19px] w-[15px] shrink-0"
                    aria-hidden="true"
                />
                Expert List
            </p>
            <h1
                class="font-brand-display text-[36px] font-black italic uppercase leading-[0.95] text-brand-navy"
            >
                {{ list.name }}
            </h1>
            <p
                v-if="list.description"
                class="mt-3 font-brand text-[13px] leading-[1.35] text-brand-navy"
            >
                {{ list.description }}
            </p>
            <div class="mt-4 flex flex-wrap items-center gap-2">
                <span
                    class="inline-flex w-fit items-center gap-1.5 rounded-[2px] border border-solid border-[#e3dcda] px-2.5 py-1 font-brand text-[11px] font-medium capitalize text-[#102a3d] opacity-60"
                >
                    <IconPrivacyPrivate
                        v-if="isPrivate"
                        class="h-[9.5px] w-[11px] shrink-0 text-[#102a3d]"
                        aria-hidden="true"
                    />
                    <IconPrivacyPublic
                        v-else
                        class="size-[10.3px] shrink-0 text-[#102a3d]"
                        aria-hidden="true"
                    />
                    {{ list.privacy }}
                </span>
                <span
                    v-if="formattedEventDate"
                    class="inline-flex w-fit items-center gap-1.5 rounded-[2px] bg-[#ffb545] px-2.5 py-1 font-brand text-[11px] font-semibold text-[#102a3d]"
                >
                    🎉 Event Date: {{ formattedEventDate }}
                </span>
            </div>
        </div>
    </header>
</template>
